"use client"

import React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import styled from "styled-components"
import { resetResponses, ASSESSMENT_TYPES } from "modules/Assessments/actions"
import selectors from "modules/Assessments/selectors"

// Container for the entire card
const Container = styled.div`
  width: 95%;
  max-width: 1400px;
  min-height: 500px;
  margin: 80px auto;
  padding: 30px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

// Header section for Title and Timer
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 500;
  color: #333333;
  margin: 0;
`

// Timer with red outline/background
const AssessmentTimer = styled.div`
  padding: 12px 24px;
  background: #fff1f1;
  border-radius: 8px;
  color: #e53e3e;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: 1px solid #e53e3e;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

// Subheader for Assessment Name
const AssessmentName = styled.div`
  color: #666666;
  font-size: 16px;
  margin-bottom: 40px;
`

const ThankYouText = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: #333333;
  margin-bottom: 20px;
`

const Message = styled.p`
  font-size: 16px;
  color: #666666;
  line-height: 1.6;
  margin: 0;
  flex-grow: 1;
`

// Next button with updated size, spacing, and hover effects
const NextButton = styled.button`
  position: relative;
  margin-top: 40px;
  padding: 14px 36px;
  background: #4640de;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: #3530ca;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`

const Completion = ({ assessment, totalTimeTaken }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const responses = useSelector(selectors.getResponses)
  const [exitingFullscreen, setExitingFullscreen] = useState(false)

  useEffect(() => {
    // Exit fullscreen
    const exitFullscreen = async () => {
      setExitingFullscreen(true)
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen()
        }

        // Restore navbar
        document.body.classList.remove('assessment-fullscreen-mode')
        const navbar = document.querySelector('nav, .navbar, .header, .app-header, .top-nav')
        if (navbar) {
          navbar.style.display = ''
        }
      } catch (error) {
        console.error('Error exiting fullscreen:', error)
      } finally {
        setExitingFullscreen(false)
      }
    }

    exitFullscreen()

    // Cleanup
    return () => {
      dispatch(resetResponses())
    }
  }, [dispatch])

  // Formats time from seconds -> mm:ss
  const formatTime = seconds => {
    if (!seconds) return '00:00'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const handleNext = () => {
    // Exit fullscreen if we're still in it
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen().catch(err => 
          console.error('Error exiting fullscreen on completion:', err)
        )
      } catch (error) {
        console.error('Error in exitFullscreen on completion:', error)
      }
    }
  
    // Simply refresh the page
    window.location.reload()
  }

  return (
    <Container>
      <Header>
        <Title>{assessment?.title || 'Custom Assessment'}</Title>
        <AssessmentTimer>
          ⏱ Time Taken : {formatTime(totalTimeTaken || 0)}
        </AssessmentTimer>
      </Header>
      
      <AssessmentName>
        Assessment Name : {assessment?.subtitle || assessment?.title || 'Custom Assessment'}
      </AssessmentName>
      
      <ThankYouText>
        Thank you for attending the assessment!
      </ThankYouText>
      
      <Message>
        Your result is being processed and will be available shortly. Please check the "Completed Assessment" tab to view your score.
      </Message>
      
      <NextButton onClick={handleNext}>
        Next
      </NextButton>
    </Container>
  )
}

export default Completion